import {
  PlatformConnectionSource,
  type PlatformConnectionChange,
} from "@afar-research/client-runtime/platform";
import {
  ClientTransportResources,
  ConnectionTransientError,
  Connectivity,
  EnvironmentWebSocketAuthorization,
  mapHostEnvironmentError,
  mintTicketOverHttp,
  PrimaryConnectionTarget,
  RemoteConnectionTarget,
  Wakeups,
} from "@afar-research/client-runtime/connection";
import type { EnvironmentAccessProfile } from "@afar-research/contracts";
import { hasSshEnvironmentBridge } from "./developmentOnlyRoutes";
import { cloudWorkspaceConnectionGatewayLayer } from "./cloudWorkspaceConnection";
import { sshEnvironmentGatewayLayer } from "./sshEnvironment";
import {
  pairedEnvironmentConnectionChanges,
  readPairedConnections,
  subscribePairedConnections,
} from "./pairedConnections";
import {
  readSshConnections,
  sshEnvironmentConnectionChanges,
  subscribeSshConnections,
} from "./sshConnections";
import {
  EnvironmentRpcRequestObserver,
  type HostEnvironmentRequestError,
} from "@afar-research/client-runtime/rpc";
import * as Effect from "effect/Effect";
import * as Exit from "effect/Exit";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Queue from "effect/Queue";
import * as Schedule from "effect/Schedule";
import * as Stream from "effect/Stream";

import { getClientSettingsSnapshot } from "../clientSettingsStore";
import { PrimaryEnvironmentHttpClient } from "../environments/primary/httpClient";
import { readDesktopPrimaryBearer } from "../environments/primary/desktopAuth";
import {
  isRuntimeWebBuild,
  isWebOnlyDevelopment,
} from "../environments/primary/browserEntry.logic";
import { browserBuildEnvironment } from "../env";
import { readPrimaryEnvironmentTarget } from "../environments/primary/target";
import { isLocalEnvironmentDisabled } from "../localEnvironment";
import { setPrimaryEnvironmentDiscovery } from "./primaryEnvironmentDiscovery";
import { runPrimaryHttp } from "../lib/runtime";
import { acknowledgeRpcRequest, trackRpcRequestSent } from "../rpc/requestLatencyState";

const connectEnvironmentIds = new Set<EnvironmentAccessProfile["environmentId"]>();
export function disconnectConnectEnvironments(): void {
  for (const id of connectEnvironmentIds) removeSavedEnvironment(id);
  connectEnvironmentIds.clear();
}

let nextObservedRpcRequestId = 0;
const REMOTE_ENVIRONMENT_CONNECTION_EVENT = "afar-research:connect-saved-environment";
const REMOTE_ENVIRONMENT_REMOVAL_EVENT = "afar-research:remove-saved-environment";

/**
 * Whether this client has a primary Environment to find. The Desktop Client always does; a
 * browser does when an Environment served it, on loopback or — when the service listens on a
 * network address — anywhere a paired browser reached it from. Runtime Web and web-only development
 * builds have no origin-local Environment.
 */
export function shouldDiscoverPrimaryEnvironment(input: {
  readonly webOnlyDevelopment: boolean;
  readonly runtimeWeb?: boolean;
  readonly hasDesktopBridge: boolean;
  readonly hostname?: string;
  /** A Desktop with its local environment turned off has no service on this machine to find. */
  readonly localEnvironmentDisabled?: boolean;
}): boolean {
  if (input.localEnvironmentDisabled === true) return false;
  return input.hasDesktopBridge || (!input.webOnlyDevelopment && input.runtimeWeb !== true);
}

// Current registration intent survives platform listener startup/restart. Profiles contain
// route provenance, never the short-lived Connect assertion or session credential.
const remoteEnvironmentProfiles = new Map<string, RemoteEnvironmentConnectionEventDetail>();

interface RemoteEnvironmentConnectionEventDetail {
  readonly profile: EnvironmentAccessProfile;
  readonly replace: boolean;
  readonly connect?: boolean;
}

/**
 * A registration for a Linked Computer.
 *
 * A remote Environment is reached through AFAR Connect, which names the endpoint when it brokers
 * the introduction (ADR-0122). The saved endpoint is kept on the target as provenance and as
 * something to show, never as the route a connection takes.
 */
export function buildRemoteHostRegistration(
  profile: EnvironmentAccessProfile,
): { readonly target: RemoteConnectionTarget; readonly enabled: boolean } | null {
  const httpUrl = new URL(profile.endpoint);
  const wsUrl = new URL(profile.endpoint);
  if (wsUrl.protocol === "https:") wsUrl.protocol = "wss:";
  else if (wsUrl.protocol === "http:") wsUrl.protocol = "ws:";
  else throw new Error("Saved AFAR Environment endpoint must use HTTP or HTTPS.");
  return {
    // Whether the user left this computer switched off is remembered per device, so a restart
    // restores the switch rather than quietly reconnecting a machine they turned off.
    enabled: !getClientSettingsSnapshot().disabledEnvironmentIds.includes(profile.environmentId),
    target: new RemoteConnectionTarget({
      environmentId: profile.environmentId,
      label: profile.environmentLabel,
      httpBaseUrl: httpUrl.toString(),
      wsBaseUrl: wsUrl.toString(),
    }),
  };
}

export function connectSavedEnvironment(
  profile: EnvironmentAccessProfile,
  options?: { readonly replace?: boolean; readonly connect?: boolean },
): void {
  const detail: RemoteEnvironmentConnectionEventDetail = {
    profile,
    replace: options?.replace === true,
    ...(options?.connect === undefined ? {} : { connect: options.connect }),
  };
  remoteEnvironmentProfiles.set(profile.environmentId, detail);
  connectEnvironmentIds.add(profile.environmentId);
  window.dispatchEvent(
    new CustomEvent<RemoteEnvironmentConnectionEventDetail>(REMOTE_ENVIRONMENT_CONNECTION_EVENT, {
      detail,
    }),
  );
}

export function buildRemoteHostConnectionChange(
  profile: EnvironmentAccessProfile,
  options?: { readonly replace?: boolean },
): PlatformConnectionChange | null {
  const registration = buildRemoteHostRegistration(profile);
  if (registration === null) return null;
  return options?.replace
    ? { _tag: "ReplaceHost", registration }
    : { _tag: "InstallHost", registration };
}

/** Skips any computer with no remembered destination, which there is no way to connect to. */
export function buildRemoteHostConnectionChanges(
  profiles: ReadonlyArray<EnvironmentAccessProfile>,
): ReadonlyArray<Extract<PlatformConnectionChange, { readonly _tag: "InstallHost" }>> {
  return profiles.flatMap((profile) => {
    const registration = buildRemoteHostRegistration(profile);
    return registration === null ? [] : [{ _tag: "InstallHost" as const, registration }];
  });
}

export function removeSavedEnvironment(
  environmentId: EnvironmentAccessProfile["environmentId"],
): void {
  remoteEnvironmentProfiles.delete(environmentId);
  connectEnvironmentIds.delete(environmentId);
  window.dispatchEvent(
    new CustomEvent(REMOTE_ENVIRONMENT_REMOVAL_EVENT, { detail: environmentId }),
  );
}

function currentNetworkStatus(): "unknown" | "offline" | "online" {
  if (typeof navigator === "undefined") {
    return "unknown";
  }
  return navigator.onLine ? "online" : "offline";
}

/** A listener this application holds for its lifetime, accounted for as a transport resource. */
const acquireTrackedSubscription = (subscribe: () => () => void, identity: string) =>
  Effect.gen(function* () {
    const resources = yield* ClientTransportResources;
    yield* Effect.acquireRelease(
      Effect.sync(() =>
        resources.register({
          ownerIdentity: "application",
          kind: "listener",
          identity,
          lifetime: "retained-owner",
        }),
      ),
      (registered) => Effect.sync(() => registered.release("owner-disposal")),
    );
    yield* Effect.acquireRelease(Effect.sync(subscribe), (unsubscribe) => Effect.sync(unsubscribe));
  });

const acquireTrackedEventListener = (
  target: EventTarget,
  type: string,
  listener: EventListenerOrEventListenerObject,
  identity: string,
) =>
  acquireTrackedSubscription(() => {
    target.addEventListener(type, listener);
    return () => target.removeEventListener(type, listener);
  }, identity);

const connectivityChanges = Stream.callback<"offline" | "online">((queue) =>
  Effect.gen(function* () {
    const online = () => Queue.offerUnsafe(queue, "online");
    const offline = () => Queue.offerUnsafe(queue, "offline");
    yield* acquireTrackedEventListener(window, "online", online, "window:online");
    yield* acquireTrackedEventListener(window, "offline", offline, "window:offline");
  }),
);

const connectivityLayer = Connectivity.layer({
  status: Effect.sync(currentNetworkStatus),
  changes: connectivityChanges,
});

const wakeupChanges = Stream.callback<"application-active">((queue) =>
  Effect.gen(function* () {
    const listener = () => {
      if (document.visibilityState === "visible") {
        Queue.offerUnsafe(queue, "application-active");
      }
    };
    yield* acquireTrackedEventListener(
      document,
      "visibilitychange",
      listener,
      "document:visibilitychange",
    );
  }),
);

const remoteEnvironmentRegistrations = Stream.callback<{
  readonly registration: NonNullable<ReturnType<typeof buildRemoteHostRegistration>>;
  readonly replace: boolean;
}>((queue) =>
  Effect.gen(function* () {
    const publish = (detail: RemoteEnvironmentConnectionEventDetail) => {
      const base = buildRemoteHostRegistration(detail.profile);
      const registration =
        base === null
          ? null
          : {
              ...base,
              enabled: detail.connect ?? base.enabled,
            };
      // A computer with no remembered destination is not registered rather than registered
      // unreachable, so the catalog never holds an entry that cannot be connected to.
      if (registration === null) return;
      Queue.offerUnsafe(queue, { registration, replace: detail.replace });
    };
    yield* acquireTrackedSubscription(() => {
      const listener = (event: Event) =>
        publish((event as CustomEvent<RemoteEnvironmentConnectionEventDetail>).detail);
      window.addEventListener(REMOTE_ENVIRONMENT_CONNECTION_EVENT, listener);
      for (const detail of remoteEnvironmentProfiles.values()) publish(detail);
      return () => window.removeEventListener(REMOTE_ENVIRONMENT_CONNECTION_EVENT, listener);
    }, `window:${REMOTE_ENVIRONMENT_CONNECTION_EVENT}`);
  }),
);

const remoteEnvironmentRemovals = Stream.callback<EnvironmentAccessProfile["environmentId"]>(
  (queue) =>
    Effect.gen(function* () {
      const listener = (event: Event) => {
        Queue.offerUnsafe(
          queue,
          (event as CustomEvent<EnvironmentAccessProfile["environmentId"]>).detail,
        );
      };
      yield* acquireTrackedEventListener(
        window,
        REMOTE_ENVIRONMENT_REMOVAL_EVENT,
        listener,
        `window:${REMOTE_ENVIRONMENT_REMOVAL_EVENT}`,
      );
    }),
);

/**
 * Every saved SSH Environment, republished whenever the saved set changes.
 *
 * What to publish is decided by `sshEnvironmentConnectionChanges`; this is the listener that asks
 * it again on every change. Whether this client can open a forward at all is settled here, once
 * per publish, because a bridge appears before the renderer runs and never after.
 */
const sshEnvironmentChanges = Stream.callback<PlatformConnectionChange>((queue) =>
  Effect.gen(function* () {
    let published: ReadonlyArray<EnvironmentAccessProfile["environmentId"]> = [];
    const publish = () => {
      const reconciled = sshEnvironmentConnectionChanges({
        saved: readSshConnections(),
        published,
        canOpenForward: hasSshEnvironmentBridge(),
      });
      for (const change of reconciled.changes) Queue.offerUnsafe(queue, change);
      published = reconciled.published;
    };
    // Publishes once now and again whenever the saved set changes, including when Client
    // Settings first arrive: before then the saved set reads as empty.
    yield* acquireTrackedSubscription(
      () => subscribeSshConnections(publish),
      "client-settings:ssh-connections",
    );
  }),
);

/** Every Environment paired by link, republished whenever that set changes. */
const pairedEnvironmentChanges = Stream.callback<PlatformConnectionChange>((queue) =>
  Effect.gen(function* () {
    let published: ReadonlyArray<EnvironmentAccessProfile["environmentId"]> = [];
    const publish = () => {
      const reconciled = pairedEnvironmentConnectionChanges({
        saved: readPairedConnections(),
        published,
      });
      for (const change of reconciled.changes) Queue.offerUnsafe(queue, change);
      published = reconciled.published;
    };
    yield* acquireTrackedSubscription(
      () => subscribePairedConnections(publish),
      "client-settings:paired-connections",
    );
  }),
);

const wakeupsLayer = Wakeups.layer({
  changes: wakeupChanges,
});

export const loadHostRegistration = Effect.fn("web.hostEnvironment.load")(function* () {
  const resolved = readPrimaryEnvironmentTarget();
  const descriptor = yield* Effect.tryPromise({
    try: () =>
      runPrimaryHttp(
        PrimaryEnvironmentHttpClient.pipe(Effect.flatMap((client) => client.metadata.descriptor())),
      ),
    catch: (error) => error as HostEnvironmentRequestError,
  }).pipe(Effect.mapError(mapHostEnvironmentError));
  return {
    target: new PrimaryConnectionTarget({
      environmentId: descriptor.environmentId,
      label: descriptor.label,
      httpBaseUrl: resolved.target.httpBaseUrl,
      wsBaseUrl: resolved.target.wsBaseUrl,
    }),
  };
});

const descriptorDiscoverySchedule = Schedule.exponential("500 millis").pipe(
  Schedule.both(Schedule.recurs(4)),
);

const loadHostRegistrationOption = loadHostRegistration().pipe(
  Effect.retry(descriptorDiscoverySchedule),
  Effect.tapError((error) =>
    Effect.logWarning("Could not discover the host Environment Service.", { error }),
  ),
  // On every exit, defects included: a Desktop whose backend is gone has no target to resolve,
  // and resolving one throws rather than fails.
  Effect.onExit((exit) =>
    Effect.sync(() =>
      setPrimaryEnvironmentDiscovery(Exit.isSuccess(exit) ? "settled" : "unreachable"),
    ),
  ),
  Effect.option,
);

const localEnvironmentRegistrations = Stream.suspend(() =>
  shouldDiscoverPrimaryEnvironment({
    webOnlyDevelopment: isWebOnlyDevelopment(browserBuildEnvironment),
    runtimeWeb: isRuntimeWebBuild(browserBuildEnvironment),
    hasDesktopBridge: Boolean(window.desktopBridge),
    hostname: window.location.hostname,
    localEnvironmentDisabled: isLocalEnvironmentDisabled(),
  })
    ? Stream.concat(
        Stream.fromEffect(loadHostRegistrationOption),
        Stream.merge(
          connectivityChanges.pipe(Stream.filter((status) => status === "online")),
          wakeupChanges,
        ).pipe(Stream.mapEffect(() => loadHostRegistrationOption)),
      ).pipe(
        Stream.filter(Option.isSome),
        Stream.map((registration) => registration.value),
      )
    : Stream.empty.pipe(
        Stream.onStart(Effect.sync(() => setPrimaryEnvironmentDiscovery("settled"))),
      ),
);

const platformConnectionSourceLayer = Layer.succeed(
  PlatformConnectionSource,
  PlatformConnectionSource.of({
    changes: Stream.merge(
      localEnvironmentRegistrations.pipe(
        Stream.map((registration) => ({ _tag: "InstallHost" as const, registration })),
      ),
      Stream.merge(
        Stream.merge(sshEnvironmentChanges, pairedEnvironmentChanges),
        Stream.merge(
          remoteEnvironmentRegistrations.pipe(
            Stream.map(({ registration, replace }) =>
              replace
                ? ({ _tag: "ReplaceHost" as const, registration } as const)
                : ({ _tag: "InstallHost" as const, registration } as const),
            ),
          ),
          remoteEnvironmentRemovals.pipe(
            Stream.map((environmentId) => ({ _tag: "RemoveHost" as const, environmentId })),
          ),
        ),
      ),
    ),
  }),
);

/**
 * The Desktop Client opens its local Environment's socket with a ticket minted from the bearer its
 * main process paired; a browser leaves this at its default and rides its session cookie.
 */
const webSocketAuthorizationLayer = Layer.succeed(
  EnvironmentWebSocketAuthorization,
  EnvironmentWebSocketAuthorization.of({
    primaryBearer: (_target, options) =>
      Effect.tryPromise({
        try: () => readDesktopPrimaryBearer(options),
        catch: () =>
          new ConnectionTransientError({
            reason: "host-unavailable",
            detail: "The Desktop Client could not pair with its local Environment yet.",
          }),
      }),
    mintTicket: mintTicketOverHttp,
  }),
);

const rpcRequestObserverLayer = Layer.succeed(
  EnvironmentRpcRequestObserver,
  EnvironmentRpcRequestObserver.of({
    observe: ({ environmentId, method }) =>
      Effect.sync(() => {
        nextObservedRpcRequestId += 1;
        const requestId = `${environmentId}:${nextObservedRpcRequestId}`;
        trackRpcRequestSent(requestId, `${method} · ${environmentId}`);
        return Effect.sync(() => {
          acknowledgeRpcRequest(requestId);
        });
      }),
  }),
);

type ConnectionPlatformLayerSource =
  | typeof cloudWorkspaceConnectionGatewayLayer
  | typeof sshEnvironmentGatewayLayer
  | typeof connectivityLayer
  | typeof wakeupsLayer
  | typeof platformConnectionSourceLayer
  | typeof webSocketAuthorizationLayer
  | typeof rpcRequestObserverLayer;

export const connectionPlatformLayer: Layer.Layer<
  Layer.Success<ConnectionPlatformLayerSource>,
  Layer.Error<ConnectionPlatformLayerSource>,
  Layer.Services<ConnectionPlatformLayerSource>
> = Layer.mergeAll(
  cloudWorkspaceConnectionGatewayLayer,
  sshEnvironmentGatewayLayer,
  connectivityLayer,
  wakeupsLayer,
  platformConnectionSourceLayer,
  webSocketAuthorizationLayer,
  rpcRequestObserverLayer,
);
